import { ColumnDef, PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getColumnDef } from '@/components/DataGrid/columns';

import { addFlags } from '@/redux/cacheSlice';
import { setDatagrid } from '@/redux/datagridSlice';
import store, { RootState } from '@/redux/store';

import { ColumnType, DatagridIdentifier, ManagedDatagridState, PaginationStateWithTotal, SearchInterface } from '@/types/DataGrid';
import { ColumnFilterWithOperator, Row } from '@/types/DataGrid';

import getCurrentIds from '../getCurrentIds';

const log = false;
const logAllUpdates = false;

interface ManagedDatagridInitialState extends Partial<ManagedDatagridState>
{
	firstPage?: Row[];
}

const defaultDatagridState: ManagedDatagridState = {
	server_driven: true,
	entityType: 'other',
	stale: false,
	invalidate: false,
	buffering: false,
	refetch: false,
	sortingState: [],
	paginationState: { pageIndex: 0, pageSize: 10, total_result_count: 0 },
	filterState: [],
	query: '',
	search: {},
	columns: [],
	columnDefs: [],
	pages: {},
	rows: [],
	selectedRows: {},
	expanded: false,
	expandedCells: {},
	columnVisibility: {},
	columnSizes: {},
	manuallyResizedColumns: []
};

//Although these are trivial functions, they infer types and make code more durable far down the line
const keyToIdentifier: (key: string) => DatagridIdentifier = (key: string) =>
{
	const [type, id] = key.split('_');
	return { type: type as DatagridIdentifier['type'], id };
};

const identifierToKey: (identifier: DatagridIdentifier) => string = (identifier: DatagridIdentifier) =>
{
	return `${identifier.type}_${identifier.id}`;
};

//When we recieve search results, they might be for an old search, so we need to check if the search is the same
const sameSearch = (currentSearch: any, resultsSearch: any) =>
{
	//When coming from the backend, an empty query might be omitted, so we need to check for that
	//!(!currentSearch.query && resultsSearch.query) => currentSearch.query || resultsSearch.query by De Morgan's law
	if (
		((typeof currentSearch.query === 'string' && currentSearch.query.length > 0) ||
			(typeof resultsSearch.query === 'string' && resultsSearch.query.length > 0)) &&
		currentSearch.query !== resultsSearch.query
	)
		return false;

	//Single sort for now, so we can just check the first element in order_by and order_direction
	if (currentSearch.order_by[0] !== resultsSearch.order_by[0]) return false;
	if (currentSearch.order_direction[0] !== resultsSearch.order_direction[0]) return false;

	//Need to check the data filters as well
	if (currentSearch.data_filters?.length !== resultsSearch.data_filters?.length) return false;

	for (let i = 0; i < currentSearch.data_filters.length; i++)
	{}

	return true;
};

const fetchPage = (identifier: DatagridIdentifier | string, page: number, addToEmit?: any) =>
{
	const reduxKey = typeof identifier === 'string' ? identifier : identifierToKey(identifier);

	//Pull everything from the redux store
	//const datagrid = useSelector((state: RootState) => state.datagrid.datagrids[reduxKey]);
	const datagrid = store.getState().datagrid.datagrids[reduxKey];

	if (!datagrid)
	{
		console.log('No datagrid found for', reduxKey, store.getState());
		return;
	}

	const { entityType, sortingState, paginationState, filterState, search, query } = datagrid;

	if (log) console.log(`[${reduxKey}]`, 'Fetching page', page, 'with', paginationState.pageSize, 'rows');

	const order_by: string[] = [];
	const order_direction: string[] = [];

	sortingState.forEach((sort: { desc: any; id: string }) =>
	{
		//if (sort.desc !== false)
		//{
		order_by.push(sort.id);
		order_direction.push(sort.desc ? 'desc' : 'asc');
		//}
	});

	const { session_id, tab_id } = getCurrentIds();

	const existing_data_filters =
		search?.data_filters?.filter((filter: any) =>
		{
			return !filter.user_filter;
		}) ?? [];

	//Append from filter state
	const user_filters = filterState
		.filter((filter: any) =>
		{
			return filter.user_filter;
		})
		.map(filter =>
		{
			if (filter.enum)
			{
				if (Array.isArray(filter.value))
				{
					return {
						key: filter.id,
						operator: filter.operator,
						value: filter.value,
						user_filter: !!filter.user_filter
					};
				}
				else
				{
					return {
						key: filter.id,
						operator: filter.operator,
						value: [filter.value],
						user_filter: !!filter.user_filter
					};
				}
			}

			return {
				key: filter.id,
				operator: filter.operator,
				value: filter.value,
				user_filter: !!filter.user_filter
			};
		});

	if (log) console.log('Constructing data filters from column filters:', user_filters, 'and existing filters:', existing_data_filters);
	const data_filters = [...existing_data_filters, ...user_filters];

	//Before fetching the page, update the search to retain the direction and sorting
	if (log)
		console.log(`RK ${reduxKey} setting search to:`, {
			...search,
			data_filters,
			order_by,
			order_direction
		});

	// Since this is a standalone function outside the hook, we need to dispatch directly
	store.dispatch(
		setDatagrid({
			identifier: reduxKey,
			state: {
				...datagrid,
				search: {
					...search,
					data_filters,
					order_by,
					order_direction
				}
			}
		})
	);

	//console.log('Data filters', data_filters,'from existing filters:', existing_data_filters, 'Column filters:', user_filters, 'from', filterState, search);

	let socket_obj: any = {
		action_type: 'search',
		search_type: entityType,
		filters: {
			...search,
			query,
			page: page,
			per_page: paginationState.pageSize,
			order_by,
			order_direction,
			data_filters
		},
		session_id,
		tab_id,
		new_search: false
	};

	if (reduxKey.includes('list_'))
	{
		const list_id = reduxKey.split('_')[1];
		socket_obj = {
			...socket_obj,
			search_type: 'list',
			list_id
		};
	}

	if (addToEmit)
	{
		socket_obj = {
			...socket_obj,
			...addToEmit
		};
	}

	//If we're fetching all pages to export the full list, we need to set the total results count, and set it in the filters per_page
	if (addToEmit?.export_full)
	{
		socket_obj.filters.per_page = socket_obj.total_results;
	}

	socket.emit('take_action', socket_obj);
};

const useManagedDatagrid = (identifier: DatagridIdentifier, initialState: ManagedDatagridInitialState) =>
{
	const reduxKey = identifierToKey(identifier);
	const datagrid = useSelector((state: RootState) => state.datagrid.datagrids[reduxKey]);
	const initialized = useRef(!!datagrid);

	const constructedInitialState: ManagedDatagridState = {
		...defaultDatagridState,
		...initialState,
		pages: initialState.firstPage ? { 1: initialState.firstPage } : defaultDatagridState.pages,
		rows: initialState.firstPage ?? defaultDatagridState.rows
	};

	//Destructure the datagrid state for easier access
	const {
		server_driven,
		entityType,
		stale,
		invalidate,
		buffering,
		refetch,
		query,
		sortingState,
		paginationState,
		filterState,
		search,
		columns,
		rows,
		pages,
		selectedRows,
		expanded,
		expandedCells,
		columnVisibility
	} = datagrid ?? constructedInitialState;

	if (logAllUpdates)
	{
		console.log(
			`[${reduxKey}] Datagrid state:`,
			datagrid,
			'initialState:',
			constructedInitialState,
			'from',
			initialState,
			'initialized:',
			initialized.current,
			'sortingState:',
			sortingState,
			'filterState:',
			filterState
		);
	}

	//Grab setters
	const {
		batchUpdate,
		setStale,
		setInvalidate,
		setBuffering,
		setRefetch,
		setSortingState,
		setPaginationState,
		setFilterState,
		setSearch,
		setColumns,
		setPages,
		setRows,
		actionsCallback,
		setQuery,
		setColumnDefs,
		setColumnVisibility,
		setExpanded,
		setColumnSizes,
		setSelectedRows
	} = useManagedDatagridSetters(identifier);

	const initialHandled = useRef({
		filterState: false,
		sortingState: false,
		pageSize: false
	});

	const identifierHash = useMemo(() => identifierToKey(identifier), [identifier]);

	//When the identifier changes, reset the initialHandled
	useEffect(() =>
	{
		initialHandled.current = {
			filterState: false,
			sortingState: false,
			pageSize: false
		};
	}, [identifierHash]);

	//Map columns to columnDefs
	const columnDefs = useMemo(() => getColumnDef(columns, reduxKey), [columns, reduxKey]);
	// console.log('Column defs:', columnDefs, 'from', columns, reduxKey);

	//Whenever columns are added, update the column visibility
	useEffect(() =>
	{
		const newCols = columns.filter(col => columnVisibility[col.key] === undefined);

		//Append new cols as true:
		const newVisibility = newCols.reduce(
			(acc, col) =>
			{
				acc[col.key] = true;
				return acc;
			},
			{ ...columnVisibility }
		);

		setColumnVisibility(newVisibility);
	}, [columns]);

	//When the pagination state, filter state, sorting state, and pages change, update the rows state -- only after the new rows are retrieved though
	useEffect(() =>
	{
		if (!initialized.current) return;

		if (!server_driven) return;

		if (!pages[paginationState?.pageIndex + 1])
		{
			console.log('Page', paginationState?.pageIndex + 1, 'not found, fetching');
			setBuffering(true);
			fetchPage(identifier, paginationState?.pageIndex + 1);
		}
	}, [paginationState, pages]);

	//When the pagination state, filter state, sorting state, and pages change, update the rows state -- only after the new rows are retrieved though
	useEffect(() =>
	{
		const index = paginationState?.pageIndex + 1;
		if (!pages || !pages[index])
		{
			if (log) console.log('Page', index, 'not found, buffering', pages);
			setBuffering(true);
			return;
		}

		if (log) console.log('Setting rows to page', index, 'with', pages[index].length, 'rows', rows, pages, paginationState);
		setRows(pages[index]);
	}, [paginationState?.pageIndex, pages]);

	//When stale, fetch the current page
	useEffect(() =>
	{
		if (!initialized.current) return;

		if (log) console.log(`[${reduxKey}] Stale state changed, fetching page`, stale);

		if (stale)
		{
			fetchPage(identifier, paginationState?.pageIndex + 1);
		}
	}, [stale]);

	//To check when the sorting state ACTUALLY changes, we need to hash the sorting state, as it's an array of objects
	const sortingStateHash = useMemo(() =>
	{
		// if (!initialized.current) return '';

		const hash = sortingState
			?.map(sort =>
			{
				return `${sort.id}-${sort.desc}`;
			})
			.join(',');

		if (log) console.log('Sorting state hash:', hash);
		return hash;
	}, [sortingState]);

	//Same for filter state:
	const filterStateHash = useMemo(() =>
	{
		// if (!initialized.current) return '';

		const hash = filterState
			?.map(filter =>
			{
				return `${filter.id}-${JSON.stringify(filter.value)}-${filter.operator}`;
			})
			.join(',');

		if (log) console.log('Filter state hash:', hash);
		return hash;
	}, [filterState]);

	useEffect(() =>
	{
		// if (!initialized.current) return;

		if (!initialHandled.current.filterState)
		{
			if (log) console.log('Filter state not handled, setting to true');
			initialHandled.current.filterState = true;
			return;
		}

		if (server_driven)
		{
			if (log) console.log('Refetching due to filter state change', filterStateHash);
			batchUpdate({
				buffering: !!server_driven,
				paginationState: {
					...paginationState,
					pageIndex: 0
				},
				selectedRows: {}
			});

			fetchPage(identifier, 1);
		}
	}, [filterStateHash]);

	useEffect(() =>
	{
		if (!initialHandled.current.sortingState)
		{
			if (log) console.log('Sorting state not handled, setting to true');
			initialHandled.current.sortingState = true;
			return;
		}

		if (log) console.log('Refetching due to sorting state change', sortingStateHash);
		//setRefetch(true);
		//Instead of setting refetch and waiting for multiple state propagation steps, set state directly

		batchUpdate({
			buffering: !!server_driven,
			paginationState: {
				...paginationState,
				pageIndex: 0
			},
			selectedRows: {}
		});

		if (server_driven)
		{
			fetchPage(identifier, 1);
		}
	}, [sortingStateHash]);

	useEffect(() =>
	{
		if (!initialHandled.current.pageSize)
		{
			if (log) console.log('Page size not handled, setting to true');
			initialHandled.current.pageSize = true;
			return;
		}

		//Page size also means refetch all
		batchUpdate({
			buffering: true,
			paginationState: {
				...paginationState,
				pageIndex: 0
			}
		});

		if (server_driven)
		{
			fetchPage(identifier, paginationState?.pageIndex + 1);
		}
	}, [paginationState?.pageSize]);

	//On refetch, reset pagination state
	useEffect(() =>
	{
		if (log) console.log(`[${reduxKey}] Refetching`, refetch);

		if (refetch)
		{
			batchUpdate({
				refetch: false,
				stale: true,
				buffering: true,
				paginationState: {
					...paginationState,
					pageIndex: 0
				}
			});
		}
	}, [refetch]);

	//Mount a socket listener for the search_results event
	useEffect(() =>
	{
		if (!initialized.current) return;

		const listener = (resultData: any) =>
		{
			//On search results, clear the advanced filter flag
			store.dispatch(
				addFlags({
					key: 'advanced-filter-trigger',
					value: false
				})
			);

			//However, only run this check if the entityType is defined for both this component and the result.
			const resultType = resultData.results.entity_type ?? resultData.search.entity_type;
			if (resultType && entityType && resultType !== entityType)
			{
				if (log) console.log('Search type mismatch result type', resultType, 'my type:', entityType, reduxKey);
				return;
			}

			//Sometimes, the search results will be from an old search.
			if (resultData.list_id && `list_${resultData.list_id}` !== reduxKey)
			{
				if (log) console.log('Retrieved results for a different list:', resultData.list_id, reduxKey);
				return;
			}
			else if (!sameSearch(resultData.search, search))
			{
				if (log) console.log('Out-of-date search results recieved:', resultData.search, search, resultData);
				return;
			}

			let newPages;
			let updates: Partial<ManagedDatagridState> = {};

			//If this is on a stale page, clear the pages first
			if (stale)
			{
				newPages = {
					[resultData.search.page]: resultData.results.results
				};
				updates.stale = false;
			}
			//Otherwise, merge the new data into the existing pages
			else
			{
				newPages = {
					...pages,
					[resultData.search.page]: resultData.results.results
				};
			}

			updates.pages = newPages;

			//Update columns if they're included in the search results
			if (resultData.results.columns && resultData.results.columns.length > 0)
			{
				// sometimes columns come back empty from backend
				//console.log('Setting columns to:', resultData.results.columns);
				updates.columns = resultData.results.columns;
			}
			else
			{
				//console.log('No columns found in updated search results', resultData);
			}

			//Insert new data into the pages
			if (log) console.log('Setting pages to:', newPages);

			//If this is the results for the current page, stop buffering
			if (resultData.search.page === paginationState?.pageIndex + 1)
			{
				updates.buffering = false;
			}
			else
			{
				console.log('Received results for non-current page:', resultData.search.page, paginationState?.pageIndex + 1);
			}

			updates.search = {
				...search,
				...resultData.search,
				page: paginationState?.pageIndex + 1
			};

			updates.paginationState = {
				...paginationState,
				total_result_count: resultData.results.total_count
			};

			// Batch all the updates together
			batchUpdate(updates);

			return;
		};

		socket.on('search_results', listener);

		return () =>
		{
			socket.off('search_results', listener);
		};
	}); // fill in the dependencies eventually

	//Initialization has to go last
	//On mount, if the initialState is not set, then set the initialState to the default state
	useEffect(() =>
	{
		if (!datagrid)
		{
			console.log('Setting initial state:', constructedInitialState, 'for', reduxKey);
			store.dispatch(
				setDatagrid({
					identifier: reduxKey,
					state: constructedInitialState
				})
			);
			initialized.current = true;
		}
	}, [datagrid, constructedInitialState]);

	//When invalidated, insert based on the passed in initialState
	useEffect(() =>
	{
		if (!initialized.current) return;

		if (invalidate)
		{
			console.log('Inserting new state:', constructedInitialState, 'from', datagrid, 'and', initialState, 'because of invalidate on', reduxKey);
			store.dispatch(setDatagrid({ identifier: reduxKey, state: constructedInitialState }));
		}
	}, [invalidate, constructedInitialState]);

	//Return the datagrid state
	return {
		...(datagrid ?? constructedInitialState),
		rows,
		columnDefs,
		setRows,
		setStale,
		setInvalidate,
		setBuffering,
		setRefetch,
		setSortingState,
		setPaginationState,
		setFilterState,
		setSearch,
		setColumns,
		setPages,
		actionsCallback,
		setQuery,
		setColumnDefs,
		setColumnVisibility,
		setExpanded,
		setColumnSizes,
		setSelectedRows,
		batchUpdate
	};
};

const useManagedDatagridSetters = (identifier: DatagridIdentifier) =>
{
	/*
	Setter methods
	*/

	const reduxKey = identifierToKey(identifier);
	const datagrid = useSelector((state: RootState) => state.datagrid.datagrids[reduxKey]);

	// New batch update function to handle multiple state updates at once
	const batchUpdate = (updates: Partial<ManagedDatagridState>) =>
	{
		const newState = { ...datagrid, ...updates };
		console.log('batchUpdate:', newState);

		store.dispatch(
			setDatagrid({
				identifier: reduxKey,
				state: { ...datagrid, ...updates }
			})
		);
	};

	//When the datagrid state changes, we will need to set sorting, filters, and pagination all at once.
	const actionsCallback = (sorting: SortingState, pagination: PaginationStateWithTotal, filters: ColumnFilterWithOperator[]) =>
	{
		console.log('actionsCallback:', sorting, pagination, filters);
		batchUpdate({
			sortingState: sorting,
			paginationState: pagination,
			filterState: filters
		});
	};

	const setColumnSizes = (columnSizes: Record<string, number>) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, columnSizes } }));
	};

	const setStale = (stale: boolean) =>
	{
		store.dispatch(
			setDatagrid({
				identifier: reduxKey,
				state: { ...datagrid, stale }
			})
		);
	};

	const setInvalidate = (invalidate: boolean) =>
	{
		store.dispatch(
			setDatagrid({
				identifier: reduxKey,
				state: { ...datagrid, invalidate }
			})
		);
	};

	const setBuffering = (buffering: boolean) =>
	{
		store.dispatch(
			setDatagrid({
				identifier: reduxKey,
				state: { ...datagrid, buffering }
			})
		);
	};

	const setRefetch = (refetch: boolean) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, refetch } }));
	};

	const setSortingState = (sortingState: SortingState) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, sortingState } }));
	};

	const setPaginationState = (paginationState: PaginationStateWithTotal) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, paginationState } }));
	};

	const setFilterState = (filterState: ColumnFilterWithOperator[]) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, filterState } }));
	};

	const setSearch = (search: SearchInterface) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, search } }));
	};

	const setColumns = (columns: ColumnType[]) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, columns } }));
	};

	const setPages = (pages: Record<number, Row[]>) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, pages } }));
	};

	const setRows = (rows: Row[]) =>
	{
		if (log) console.log('Setting rows:', rows, 'against', datagrid);
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, rows } }));
	};

	const setQuery = (query: string) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, query } }));
	};

	const setColumnDefs = (columnDefs: ColumnDef<Row>[]) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, columnDefs } }));
	};

	const setColumnVisibility = (columnVisibility: Record<string, boolean>) =>
	{
		if (log) console.log('Setting column visibility:', columnVisibility);

		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, columnVisibility } }));
	};

	const setExpanded = (expanded: boolean) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, expanded } }));
	};

	const setManuallyResizedColumns = (manuallyResizedColumns: string[]) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, manuallyResizedColumns } }));
	};

	const addManuallyResizedColumn = (column: string) =>
	{
		const manuallyResizedColumns = [...datagrid.manuallyResizedColumns, column].filter((col, index, self) => self.indexOf(col) === index);
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, manuallyResizedColumns: [...manuallyResizedColumns, column] } }));
	};

	const setSelectedRows = (selectedRows: RowSelectionState) =>
	{
		store.dispatch(setDatagrid({ identifier: reduxKey, state: { ...datagrid, selectedRows } }));
	};

	return {
		batchUpdate,
		setStale,
		setInvalidate,
		setBuffering,
		setRefetch,
		setSortingState,
		setPaginationState,
		setFilterState,
		setSearch,
		setColumns,
		setPages,
		setRows,
		actionsCallback,
		setQuery,
		setColumnDefs,
		setColumnVisibility,
		setExpanded,
		setColumnSizes,
		setManuallyResizedColumns,
		addManuallyResizedColumn,
		setSelectedRows
	};
};

const getManagedDatagridSetters = (identifier: DatagridIdentifier) =>
{
	const reduxKey = identifierToKey(identifier);

	// Helper to get current datagrid state at call time
	const getCurrentDatagrid = () => store.getState().datagrid.datagrids[reduxKey];

	// New batch update function to handle multiple state updates at once
	const batchUpdate = (updates: Partial<ManagedDatagridState>) =>
	{
		const datagrid = getCurrentDatagrid();
		const newState = { ...datagrid, ...updates };
		if (log) console.log('batchUpdate:', newState);

		store.dispatch(
			setDatagrid({
				identifier: reduxKey,
				state: newState
			})
		);
	};

	// When the datagrid state changes, we will need to set sorting, filters, and pagination all at once.
	const actionsCallback = (sorting: SortingState, pagination: PaginationStateWithTotal, filters: ColumnFilterWithOperator[]) =>
	{
		if (log) console.log('actionsCallback:', sorting, pagination, filters);
		batchUpdate({
			sortingState: sorting,
			paginationState: pagination,
			filterState: filters
		});
	};

	// Create setter functions that get current state at call time
	const createSetter =
		<T extends keyof ManagedDatagridState>(key: T) =>
		(value: ManagedDatagridState[T]) =>
		{
			const datagrid = getCurrentDatagrid();
			store.dispatch(
				setDatagrid({
					identifier: reduxKey,
					state: { ...datagrid, [key]: value }
				})
			);
		};

	return {
		getCurrentDatagrid,
		batchUpdate,
		setStale: createSetter('stale'),
		setInvalidate: createSetter('invalidate'),
		setBuffering: createSetter('buffering'),
		setRefetch: createSetter('refetch'),
		setSortingState: createSetter('sortingState'),
		setPaginationState: createSetter('paginationState'),
		setFilterState: createSetter('filterState'),
		setSearch: createSetter('search'),
		setColumns: createSetter('columns'),
		setPages: createSetter('pages'),
		setRows: createSetter('rows'),
		actionsCallback,
		setQuery: createSetter('query'),
		setColumnDefs: createSetter('columnDefs'),
		setColumnVisibility: createSetter('columnVisibility'),
		setExpanded: createSetter('expanded'),
		setColumnSizes: createSetter('columnSizes'),
		setManuallyResizedColumns: createSetter('manuallyResizedColumns'),
		setSelectedRows: createSetter('selectedRows')
	};
};

export default useManagedDatagrid;
export { keyToIdentifier, identifierToKey, fetchPage, useManagedDatagridSetters, getManagedDatagridSetters };
export type { ManagedDatagridInitialState };
